/**
 * @cfleet/api-client - Customer Organization Hooks
 *
 * React Query hooks for customer organization management operations.
 * These endpoints require customer authentication.
 *
 * Organizations represent partner delivery companies that customers
 * can dispatch orders to. This hook is used for multi-tenant order filtering.
 *
 * @example
 * ```tsx
 * import {
 *   useCustomerOrganizations,
 *   useCustomerOrganization,
 * } from '@cfleet/api-client';
 *
 * // List organizations with pagination
 * const { data } = useCustomerOrganizations({ page: 1, per_page: 20 }, { authConfig });
 *
 * // Get single organization with stats
 * const { data } = useCustomerOrganization('org_001', { authConfig });
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import createClient from 'openapi-fetch';
import type { CustomerOrganizationsPaths, CustomerOrganizationsComponents } from '../generated';
import { createAuthMiddleware, type AuthMiddlewareConfig } from '../middleware';

// ─────────────────────────────────────────────────────────────────────────────
// Type Aliases from Generated Types
// ─────────────────────────────────────────────────────────────────────────────

type CustomerOrganization = CustomerOrganizationsComponents['schemas']['CustomerOrganization'];
type OrganizationListResponse =
  CustomerOrganizationsComponents['schemas']['OrganizationListResponse'];
type OrganizationDetailResponse =
  CustomerOrganizationsComponents['schemas']['OrganizationDetailResponse'];
type OrganizationStatus = CustomerOrganizationsComponents['schemas']['OrganizationStatus'];
type OrganizationStats = CustomerOrganizationsComponents['schemas']['OrganizationStats'];
type ErrorResponse = CustomerOrganizationsComponents['schemas']['ErrorResponse'];

// Re-export types for consumers
export type {
  CustomerOrganization,
  OrganizationListResponse,
  OrganizationDetailResponse,
  OrganizationStatus,
  OrganizationStats,
};

// ─────────────────────────────────────────────────────────────────────────────
// Client Factory
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a client specifically for customer organizations operations.
 * This avoids type conflicts from combining multiple path types.
 */
function createCustomerOrganizationsClient(authConfig: AuthMiddlewareConfig) {
  const client = createClient<CustomerOrganizationsPaths>({
    baseUrl: authConfig.baseUrl,
  });
  client.use(createAuthMiddleware(authConfig));
  return client;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook Configuration Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration required for all customer organization hooks.
 * The authConfig provides authentication and base URL settings.
 */
export interface UseCustomerOrganizationHooksConfig {
  authConfig: AuthMiddlewareConfig;
}

/**
 * Parameters for listing customer organizations.
 */
export interface ListCustomerOrganizationsParams {
  /** Page number (1-based) */
  page?: number;
  /** Items per page */
  per_page?: number;
  /** Filter by organization status */
  status?: OrganizationStatus;
}

// ─────────────────────────────────────────────────────────────────────────────
// Query Key Factory
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Query key factory for customer organization queries.
 * Provides consistent query key generation for caching and invalidation.
 */
export const customerOrganizationQueryKeys = {
  all: ['customer-organizations'] as const,
  lists: () => [...customerOrganizationQueryKeys.all, 'list'] as const,
  list: (params: ListCustomerOrganizationsParams) =>
    [...customerOrganizationQueryKeys.lists(), params] as const,
  details: () => [...customerOrganizationQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerOrganizationQueryKeys.details(), id] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to fetch a paginated list of partner organizations.
 *
 * Returns organizations the customer has access to, with optional
 * filtering by status (Active/Disabled).
 *
 * @example
 * ```tsx
 * const { data, isLoading, isError } = useCustomerOrganizations(
 *   { page: 1, per_page: 20, status: 'Active' },
 *   { authConfig }
 * );
 *
 * // Access organizations
 * const organizations = data?.data ?? [];
 *
 * // Access pagination info
 * const { total_items, total_pages } = data?.pagination ?? {};
 *
 * // Access summary stats
 * const { total_active, total_disabled } = data?.summary ?? {};
 * ```
 */
export function useCustomerOrganizations(
  params: ListCustomerOrganizationsParams = {},
  config: UseCustomerOrganizationHooksConfig
) {
  return useQuery({
    queryKey: customerOrganizationQueryKeys.list(params),
    queryFn: async (): Promise<OrganizationListResponse> => {
      const client = createCustomerOrganizationsClient(config.authConfig);

      const { data, error } = await client.GET('/customer/organizations', {
        params: { query: params },
      });

      if (error) {
        const apiError = error as ErrorResponse;
        throw new Error(apiError.detail || 'Failed to fetch organizations');
      }

      return data!;
    },
    enabled: !!config?.authConfig,
  });
}

/**
 * Hook to fetch a single organization with detailed stats.
 *
 * Returns organization details including performance statistics
 * (total orders, completed orders, completion rate) and recent orders.
 *
 * @example
 * ```tsx
 * const { data, isLoading, isError } = useCustomerOrganization(
 *   'org_test_001',
 *   { authConfig }
 * );
 *
 * // Access organization info
 * const { name, status, contact_email } = data?.organization ?? {};
 *
 * // Access stats
 * const { total_orders, completed_orders, completion_rate } = data?.stats ?? {};
 *
 * // Access recent orders
 * const recentOrders = data?.recent_orders ?? [];
 * ```
 */
export function useCustomerOrganization(
  orgId: string | null | undefined,
  config: UseCustomerOrganizationHooksConfig
) {
  return useQuery({
    queryKey: customerOrganizationQueryKeys.detail(orgId ?? ''),
    queryFn: async (): Promise<OrganizationDetailResponse> => {
      const client = createCustomerOrganizationsClient(config.authConfig);

      const { data, error } = await client.GET('/customer/organizations/{orgId}', {
        params: { path: { orgId: orgId! } },
      });

      if (error) {
        const apiError = error as ErrorResponse;
        throw new Error(apiError.detail || 'Failed to fetch organization');
      }

      return data!;
    },
    enabled: !!config?.authConfig && !!orgId,
  });
}
